/**
 * SearchPlaybackPanel — a small docked recording player for one search result.
 *
 * "This event, on this camera, at this moment — show me the footage." The panel opens over the
 * results grid rather than navigating away, so an operator can play one hit, dismiss it and try
 * the next without losing their query or their scroll position.
 *
 * ─── One player, both dashboards ───
 * The media comes from `RecordingPlaybackPlayer`, the shared HLS player — which carries the
 * chunk-availability handling that matters here: chunks are transcoded ON DEMAND, so a first
 * request can take a minute, a scrub cancels transcodes, and retention deletes segments under a
 * playing timeline. All of that looks like "a chunk 404'd", and retrying forever produces a
 * permanent "Buffering…" with no explanation.
 *
 * This panel owns the transport (play/pause, seek, rate) by driving the `<video>` element through
 * a ref it passes down. The transport needs no knowledge of how the media got there, so it works
 * identically wherever the panel is used.
 *
 * It opens PLAYING, and the picture itself is a play/pause target as well as the transport bar —
 * an operator scanning results wants the footage moving the moment it appears, and wants to stop
 * it where they are looking, not by finding a button underneath.
 *
 * ─── It plays a CLIP, not the segment ───
 * The node's playlist is made of whole 5-minute segments — every one overlapping the window — so
 * handed the playlist alone the player started at the segment's first second and ran for five
 * minutes, however short the window asked for. Given the event time and `loadSegments`, the panel
 * maps [event − lead, event + tail] onto the playlist (the same offset table the Recorded wall
 * uses), starts loading AT the clip, pauses at its end, and keeps the timer, the skips and Replay
 * inside it. "Open in Recordings" is still there for anything beyond the clip. Without segments
 * (or if they cannot be read) it falls back to playing the playlist as before.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  ArrowPathIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, ArrowTopRightOnSquareIcon,
  ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon, VideoCameraSlashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { SpeedPill } from '../recordings/PlaybackTransport';
import { Tip } from '../common/Tooltip';
import RecordingPlaybackPlayer from '../recordings/RecordingPlaybackPlayer';
import { buildOffsetTable, footageAt, nextFootageAfter, playableEnd, playerToWall, wallToPlayer, type OffsetRow } from '../recordings/playbackOffsets';
import { nudgeStalledSeek } from '../recordings/seekNudge';
import type { RecordingSegment } from '../../api/contracts';

/** Speeds the transport steps through. Mirrors the recordings page so the controls feel the same. */
const RATES = [0.25, 0.5, 1, 2, 4, 8];

/** One frame at the cameras' usual 25 fps — the step for frame-by-frame sync checks. */
const FRAME_SEC = 0.04;

/** Remembered panel size. Per viewer; storage can throw (private mode, blocked site data). */
const SIZE_KEY = 'searchPlayback.large';
const readLarge = () => { try { return localStorage.getItem(SIZE_KEY) === '1'; } catch { return false; } };
const writeLarge = (v: boolean) => { try { localStorage.setItem(SIZE_KEY, v ? '1' : '0'); } catch { /* ignore */ } };

export interface SearchPlaybackPanelProps {
  /** Heading — usually the camera name. */
  title: string;
  /** Sub-heading — what the event is ("Person detection"). Its time is shown by the panel. */
  subtitle?: string;
  /** The HLS playlist for the window, from `api.search.playbackUrl`. Null = nothing to play. */
  src: string | null;
  /** Seconds of footage before the event the clip starts at. */
  leadSeconds: number;
  /** Seconds after the event the clip ends at. */
  tailSeconds?: number;
  /** The event's wall-clock time, epoch ms — what the clip is centred on. */
  eventMs?: number;
  /** The playlist's window, epoch ms — must be what `src` was built for (the offset table mirrors it). */
  windowMs?: { from: number; to: number };
  /** The camera's segments over the window. Without it the whole playlist plays. */
  loadSegments?: () => Promise<RecordingSegment[]>;
  /** Longer timeouts, for a stream crossing the cloud's WebSocket tunnel. */
  tunnelled?: boolean;
  /** Bearer token for the playlist/segment fetches, read per request. See the player. */
  getAuthToken?: () => string | null;
  /** Open the full recordings page at this moment — the escape hatch to real review tooling. */
  onOpenFull?: () => void;
  onClose: () => void;
  /**
   * Render in place instead of docked over the page — the alert record's "Clip ±10s" view.
   *
   * Inline it lives inside a record that already owns Escape (closes the record), ← → (steps
   * between records) and R (resolves the alert), so it binds only the keys nothing else there
   * claims: Space/K, `,` `.` and E. The size toggle goes too: its container sets the size.
   */
  inline?: boolean;
  /** Inline only: the picture's box (default `aspect-video`). */
  frameClassName?: string;
}

export default function SearchPlaybackPanel({
  title,
  subtitle,
  src,
  leadSeconds,
  tailSeconds = 10,
  eventMs,
  windowMs,
  loadSegments,
  tunnelled = false,
  getAuthToken,
  onOpenFull,
  onClose,
  inline = false,
  frameClassName,
}: SearchPlaybackPanelProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  /** OBSERVED: what the element is actually doing. Drives the icons. */
  const [isPlaying, setIsPlaying] = useState(false);
  /**
   * INTENT: whether the operator wants this playing. Drives autoplay.
   *
   * Deliberately NOT the same state as `isPlaying`, and the panel used to pass that one to the
   * player instead — which could not work. `isPlaying` starts false and only becomes true when
   * the element fires `play`; the player only calls `play()` when this flag is already true. So
   * each waited on the other and the panel opened paused, every time, however many times you
   * clicked Playback.
   *
   * Starting at `true` is what makes opening a result play it. Pausing clears it, so a
   * mid-stream reload does not override a deliberate pause; a NEW result sets it again below.
   */
  const [wantPlaying, setWantPlaying] = useState(true);
  const [rate, setRate] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  /**
   * Whether there is a decoded frame to act on yet.
   *
   * Gates the click-to-play badge only. The player centres its own "Buffering…" notice, and the
   * badge would land on top of it during the first load — two overlapping messages where the
   * honest one is "still fetching". On-demand transcoding makes that first wait long enough to
   * see, so it is worth suppressing rather than letting them collide.
   */
  const [ready, setReady] = useState(false);

  /**
   * The clip, in PLAYER seconds, plus the table that maps it back to wall-clock time.
   * undefined = still working it out (the player waits, so it can start loading at the clip);
   * null = no clip — play the whole playlist, the old behaviour.
   */
  const [clip, setClip] = useState<{ start: number; end: number; rows: OffsetRow[] } | null | undefined>(
    loadSegments && eventMs != null && windowMs ? undefined : null,
  );
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    if (!loadSegments || eventMs == null || !windowMs) { setClip(null); return undefined; }
    let cancelled = false;
    loadSegments()
      .then((segs) => {
        if (cancelled) return;
        const rows = buildOffsetTable(segs || [], windowMs.from, windowMs.to);
        if (!rows.length) { setClip(null); return; }
        const want = eventMs - leadSeconds * 1000;
        // No footage at the clip start — a gap, or the few seconds at a segment's end that its
        // playlist drops. Take whichever footage is NEARER: where it resumes, or the last
        // clip-length before it stops. Falling back to 0 played the segment's first second —
        // minutes away from the event.
        const clipLen = leadSeconds + tailSeconds;
        let start: number;
        if (footageAt(rows, want)) {
          start = wallToPlayer(rows, want);
        } else {
          const next = nextFootageAfter(rows, want);
          const prev = [...rows].reverse().find((r) => playableEnd(r) <= want) || null;
          const dNext = next ? next.wallStart - want : Infinity;
          const dPrev = prev ? want - playableEnd(prev) : Infinity;
          if (next && dNext <= dPrev) start = next.playerStart;
          else if (prev) start = Math.max(prev.playerStart, prev.playerEnd - clipLen);
          else { setClip(null); return; }
        }
        const total = rows[rows.length - 1].playerEnd;
        // Player-time is contiguous across segments (gaps collapse), so the clip's length in
        // player seconds IS its length on screen.
        const end = Math.min(total, start + leadSeconds + tailSeconds);
        setClip(end - start >= 1 ? { start, end, rows } : null);
      })
      .catch(() => { if (!cancelled) setClip(null); });
    return () => { cancelled = true; };
    // One clip per panel: the panel is keyed on the event, so these do not change under it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clipRef = useRef(clip);
  clipRef.current = clip;

  // ── Keep our state in step with the element, whoever changed it ──
  //
  // hls.js and the browser both pause and resume on their own (buffer stalls, segment
  // boundaries, the user hitting the native controls). Mirroring the element's events rather
  // than assuming our button is the only cause is what stops the icon saying "pause" over a
  // stopped video.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => {
      const c = clipRef.current;
      const t = v.currentTime || 0;
      if (c) {
        // The clip is over: stop on its last frame rather than running on into the segment.
        if (t >= c.end - 0.05) {
          if (!v.paused) v.pause();
          setWantPlaying(false);
          setAtEnd(true);
        } else if (t < c.end - 0.5) {
          setAtEnd(false);
        }
        setElapsed(Math.max(0, Math.min(c.end, t) - c.start));
        setDuration(c.end - c.start);
        return;
      }
      setElapsed(t);
      if (Number.isFinite(v.duration)) setDuration(v.duration);
    };
    // A seek that lands in the hole around a segment boundary never completes — step it out.
    let cancelNudge: (() => void) | null = null;
    const onSeeking = () => { cancelNudge?.(); cancelNudge = nudgeStalledSeek(v); };
    v.addEventListener('seeking', onSeeking);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('durationchange', onTime);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('durationchange', onTime);
      v.removeEventListener('seeking', onSeeking);
      cancelNudge?.();
    };
  }, [src, clip]);

  // A different result means a fresh intent to watch: opening the next hit should play it even
  // if the previous one was left paused.
  useEffect(() => { setWantPlaying(true); setReady(false); }, [src]);

  // The rate is applied by RecordingPlaybackPlayer (its `playbackRate` prop, re-asserted per
  // source). Writing element.playbackRate here as well would fight its high-speed buffer gate,
  // which holds playback at rate 0 while it fills.

  // Escape closes. A panel that overlays content and can only be dismissed by hitting a small
  // button is the kind of thing people end up reloading the page to escape.
  const [large, setLarge] = useState(readLarge);
  const toggleLarge = useCallback(() => setLarge((v) => { writeLarge(!v); return !v; }), []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // Intent is recorded from the CLICK, not from the resulting element event: if the play()
    // below is refused by autoplay policy no `play` event ever fires, and reading intent back
    // off the element would silently discard what the operator asked for.
    if (v.paused) {
      setWantPlaying(true);
      // At the end of the clip, Play means "again", not "carry on into the rest of the segment".
      const c = clipRef.current;
      if (c && (v.currentTime || 0) >= c.end - 0.3) {
        v.currentTime = c.start;
        setAtEnd(false);
      }
      // The promise is caught, not awaited: an autoplay rejection is a normal outcome (a browser
      // policy, not a fault), and an unhandled rejection would surface as a console error on a
      // panel that is otherwise working.
      v.play().catch(() => { /* blocked by policy; the user can press again */ });
    } else {
      setWantPlaying(false);
      v.pause();
    }
  }, []);

  const skip = useCallback((delta: number) => {
    const v = videoRef.current;
    if (!v) return;
    const c = clipRef.current;
    // Inside the clip: ±10 s / ±30 s cannot leave it — "Open in Recordings" is for more.
    const lo = c ? c.start : 0;
    const hi = c ? c.end - 0.1 : (Number.isFinite(v.duration) ? v.duration : Infinity);
    v.currentTime = Math.max(lo, Math.min(hi, (v.currentTime || 0) + delta));
  }, []);

  const replay = useCallback(() => {
    const v = videoRef.current;
    const c = clipRef.current;
    if (!v) return;
    v.currentTime = c ? c.start : 0;
    setAtEnd(false);
    setWantPlaying(true);
    v.play().catch(() => { /* policy */ });
  }, []);

  const stepRate = useCallback((dir: 1 | -1) => {
    setRate((cur) => {
      const i = RATES.indexOf(cur);
      const next = i === -1 ? 1 : Math.max(0, Math.min(RATES.length - 1, i + dir));
      return RATES[next];
    });
  }, []);

  /** Seek to a position IN THE CLIP (seconds from its start), clamped inside it. */
  const seekClip = useCallback((sec: number) => {
    const v = videoRef.current;
    if (!v) return;
    const c = clipRef.current;
    const lo = c ? c.start : 0;
    const hi = c ? c.end - 0.1 : (Number.isFinite(v.duration) ? v.duration : Infinity);
    v.currentTime = Math.max(lo, Math.min(hi, lo + sec));
    setAtEnd(false);
  }, []);

  /** Step one frame: pauses first, since stepping a moving picture means nothing. */
  const stepFrame = useCallback((dir: 1 | -1) => {
    const v = videoRef.current;
    if (!v) return;
    if (!v.paused) { setWantPlaying(false); v.pause(); }
    skip(dir * FRAME_SEC);
  }, [skip]);

  /** Where the event sits in the clip, seconds from its start. */
  const eventInClip = clip && eventMs != null
    ? Math.max(0, Math.min(clip.end - clip.start, wallToPlayer(clip.rows, eventMs) - clip.start))
    : leadSeconds;

  const jumpToEvent = useCallback(() => seekClip(eventInClip), [seekClip, eventInClip]);

  // Keyboard, while the panel is open. Ignored while typing in a field (the query bar stays
  // usable), and Space/Enter are left alone on a focused button so they don't fire twice.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (!inline) onClose(); return; }
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if ((e.key === ' ' || e.key === 'Enter') && t?.tagName === 'BUTTON') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inline && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'r')) return;
      let handled = true;
      switch (e.key) {
        case ' ': case 'k': togglePlay(); break;
        case 'ArrowLeft': skip(e.shiftKey ? -5 : -1); break;
        case 'ArrowRight': skip(e.shiftKey ? 5 : 1); break;
        case ',': stepFrame(-1); break;
        case '.': stepFrame(1); break;
        case 'e': jumpToEvent(); break;
        case 'r': replay(); break;
        default: handled = false;
      }
      if (handled) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, togglePlay, skip, stepFrame, jumpToEvent, replay, inline]);

  const slower = RATES[Math.max(0, RATES.indexOf(rate) - 1)];
  const faster = RATES[Math.min(RATES.length - 1, RATES.indexOf(rate) + 1)];

  /**
   * Where the playhead sits relative to the EVENT, not the clip.
   *
   * The window starts before the event so there is context leading up to it, which means clip
   * time and event time are not the same thing. Showing "-8s / +12s" answers the question the
   * operator actually has — am I before or after the moment I searched for.
   */
  const offset = clip && eventMs != null
    ? ((playerToWall(clip.rows, clip.start + elapsed) ?? eventMs) - eventMs) / 1000
    : elapsed - leadSeconds;
  const offsetLabel = `${offset >= 0 ? '+' : '−'}${Math.abs(offset).toFixed(1)}s`;

  return (
    <div
      className={inline
        ? 'w-full overflow-hidden rounded-xl border border-border bg-card'
        : `fixed bottom-4 right-4 z-50 ${large ? 'w-[48rem]' : 'w-[28rem]'} max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-card shadow-2xl transition-[width] duration-200`}
      role={inline ? 'region' : 'dialog'}
      aria-label={`Playback — ${title}`}
    >
      {/* Two lines, nothing said twice: camera + actions, then what / which day / both clocks. */}
      <div className="border-b border-border px-3 py-1.5">
        <div className="flex items-center gap-1">
          <div className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{title}</div>
          {!inline && (
            <HeaderButton label={large ? 'Smaller player' : 'Larger player'} onClick={toggleLarge}>
              {large ? <ArrowsPointingInIcon className="h-3.5 w-3.5" /> : <ArrowsPointingOutIcon className="h-3.5 w-3.5" />}
            </HeaderButton>
          )}
          {onOpenFull && (
            <HeaderButton label="Open in Recordings (full timeline)" onClick={onOpenFull}>
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </HeaderButton>
          )}
          <HeaderButton label={inline ? 'Back to the frame' : 'Close (Esc)'} onClick={onClose}>
            <XMarkIcon className="h-3.5 w-3.5" />
          </HeaderButton>
        </div>
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-[10px] leading-snug text-muted-foreground">
          {subtitle && <span className="truncate">{subtitle}</span>}
          {eventMs != null && <span className="font-mono">{formatDay(eventMs)}</span>}
          {src && !error && clip !== undefined && eventMs != null && (
            <PlaybackTimes videoRef={videoRef} rows={clip ? clip.rows : null} eventMs={eventMs} />
          )}
        </div>
      </div>

      <div className={`relative bg-black ${inline && frameClassName ? frameClassName : 'aspect-video'}`}>
        {src && !error && clip !== undefined && (
          <RecordingPlaybackPlayer
            src={src}
            startPosition={clip ? clip.start : undefined}
            videoRef={videoRef}
            autoPlay
            tunnelled={tunnelled}
            getAuthToken={getAuthToken}
            playbackRate={rate}
            wantPlaying={wantPlaying}
            onCanPlay={() => setReady(true)}
            onError={setError}
          />
        )}
        {src && !error && clip === undefined && (
          <div className="flex h-full items-center justify-center gap-2 text-[11px] text-white/70">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            Finding the clip…
          </div>
        )}
        {src && !error && ready && atEnd && (
          <button
            type="button"
            onClick={replay}
            aria-label="Replay the clip"
            className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/50 text-white focus:outline-none"
          >
            <span className="rounded-full bg-black/60 p-3"><PlayIcon className="h-6 w-6" /></span>
            <span className="text-[11px] font-medium">Replay clip</span>
          </button>
        )}
        {src && !error && ready && !atEnd && (
          /* Click the picture to play or pause, the way every video behaves. The transport
             below stays the precise control (skip, rate); this is the one an operator reaches
             for without looking. Transparent while playing so the player's own "Buffering…"
             notice still reads through it, and the badge fades in on hover to advertise that
             the picture is clickable at all. */
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="group absolute inset-0 flex items-center justify-center focus:outline-none"
          >
            <span
              className={`rounded-full bg-black/55 p-3 text-white transition-opacity duration-150
                ${isPlaying ? 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100' : 'opacity-100'}`}
            >
              {isPlaying ? <PauseIcon className="h-6 w-6" /> : <PlayIcon className="h-6 w-6" />}
            </span>
          </button>
        )}
        {(!src || error) && (
          /* Two different "nothing to play" cases, and the difference matters to the operator.
             No URL at all: recording was off, or the host cannot resolve one. An error: the
             player tried and the server told it why — retention, a failed transcode, a
             still-running one. The player's message is more specific than anything this panel
             could guess, so it is shown verbatim. */
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <VideoCameraSlashIcon className="h-7 w-7 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              {error || 'No recording available for this moment — recording may have been off on '
                + 'this camera, or the footage has passed its retention window.'}
            </p>
            {error && (
              <button
                type="button"
                onClick={() => setError(null)}
                className="mt-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground hover:bg-muted"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>

      {src && !error && (
        <div className="flex flex-col gap-2 px-3 pb-2.5 pt-2">
          <ClipScrubber
            elapsed={elapsed}
            duration={duration}
            eventAt={clip ? eventInClip : null}
            onSeek={seekClip}
          />
          <div className="flex items-center justify-between font-mono text-[10px] tabular-nums text-muted-foreground">
            <span>
              {formatClock(elapsed)}{duration > 0 ? ` / ${formatClock(duration)}` : ''}
            </span>
            <span title="Playhead position relative to the event">
              <span className={Math.abs(offset) < 0.5 ? 'font-semibold text-amber-500' : ''}>event {offsetLabel}</span>
              {clip && <span className="ml-1.5 opacity-70">· clip −{leadSeconds}s / +{tailSeconds}s</span>}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <CtrlButton label="Back 1 second (←)" onClick={() => skip(-1)}>−1s</CtrlButton>
              <CtrlButton label="Previous frame (,)" onClick={() => stepFrame(-1)}>
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              </CtrlButton>
              <Tip label={`${isPlaying ? 'Pause' : atEnd ? 'Replay' : 'Play'} (Space)`}>
                <button
                  type="button"
                  onClick={atEnd ? replay : togglePlay}
                  aria-label={isPlaying ? 'Pause' : atEnd ? 'Replay' : 'Play'}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-white shadow-sm transition-colors hover:bg-brand-dark"
                >
                  {isPlaying ? <PauseIcon className="h-[18px] w-[18px]" />
                    : atEnd ? <ArrowPathIcon className="h-[18px] w-[18px]" />
                      : <PlayIcon className="h-[18px] w-[18px] translate-x-px" />}
                </button>
              </Tip>
              <CtrlButton label="Next frame (.)" onClick={() => stepFrame(1)}>
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </CtrlButton>
              <CtrlButton label="Forward 1 second (→)" onClick={() => skip(1)}>+1s</CtrlButton>
            </div>
            <div className="flex items-center gap-1.5">
              {clip && (
                <Tip label="Jump to the event (E)">
                  <button
                    type="button"
                    onClick={jumpToEvent}
                    className="flex h-7 items-center gap-1 rounded-lg border border-amber-500/50 px-2 text-[11px] font-medium text-amber-600 transition-colors hover:bg-amber-500/10 dark:text-amber-400"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Event
                  </button>
                </Tip>
              )}
              <SpeedPill
                compact
                playbackRate={rate}
                onSpeedUp={() => stepRate(1)}
                onSlowDown={() => stepRate(-1)}
                slowerSpeed={slower}
                fasterSpeed={faster}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Small square transport button with a themed tooltip. */
function CtrlButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Tip label={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="flex h-7 min-w-[1.75rem] items-center justify-center rounded-md px-1.5 font-mono text-[11px] text-foreground transition-colors hover:bg-muted"
      >
        {children}
      </button>
    </Tip>
  );
}

/**
 * The clip's progress bar: click or drag to seek, with the event marked where it falls.
 *
 * Positions are CLIP seconds (0 … duration). The fill follows `elapsed` from timeupdate, which
 * is coarse (~4×/s) — a short CSS transition smooths it, dropped while dragging so the thumb
 * stays under the pointer.
 */
function ClipScrubber({ elapsed, duration, eventAt, onSeek }: {
  elapsed: number;
  duration: number;
  eventAt: number | null;
  onSeek: (sec: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const dur = duration > 0 ? duration : 0;
  const at = (clientX: number) => {
    const el = trackRef.current;
    if (!el || !dur) return 0;
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * dur;
  };
  const pct = (sec: number) => (dur ? `${(Math.max(0, Math.min(dur, sec)) / dur) * 100}%` : '0%');
  const shown = drag ?? elapsed;
  const tipSec = drag ?? hover;
  const rel = (sec: number) => {
    if (eventAt == null) return formatClock(sec);
    const d = sec - eventAt;
    return `event ${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(1)}s`;
  };

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label="Seek within the clip"
      aria-valuemin={0}
      aria-valuemax={Math.round(dur)}
      aria-valuenow={Math.round(shown)}
      tabIndex={-1}
      className="group relative h-4 cursor-pointer touch-none select-none"
      onPointerDown={(e) => {
        if (!dur) return;
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        const s = at(e.clientX);
        setDrag(s);
        onSeek(s);
      }}
      onPointerMove={(e) => {
        const s = at(e.clientX);
        setHover(s);
        if (drag != null) { setDrag(s); onSeek(s); }
      }}
      onPointerUp={() => setDrag(null)}
      onPointerCancel={() => setDrag(null)}
      onPointerLeave={() => setHover(null)}
    >
      <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted transition-[height] group-hover:h-1.5">
        <div
          className={`h-full rounded-full bg-brand ${drag == null ? 'transition-[width] duration-200 ease-linear' : ''}`}
          style={{ width: pct(shown) }}
        />
      </div>
      {eventAt != null && dur > 0 && (
        <div
          className="pointer-events-none absolute top-0 h-4 w-0.5 -translate-x-1/2 rounded bg-amber-500"
          style={{ left: pct(eventAt) }}
          title="Event"
        />
      )}
      <div
        className={`pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-brand shadow
          ${drag != null ? 'scale-110' : 'scale-0 group-hover:scale-100'} transition-transform`}
        style={{ left: pct(shown) }}
      />
      {tipSec != null && dur > 0 && (
        <div
          className="pointer-events-none absolute bottom-full mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white"
          style={{ left: pct(tipSec) }}
        >
          {rel(tipSec)}
        </div>
      )}
    </div>
  );
}

/**
 * Two clocks in the title bar, so sync can be checked frame by frame without covering the
 * camera's own on-screen clock:
 *   Event   — the event's `event_timestamp`, the reference the clip is cut around.
 *   Playing — the wall-clock time of the frame on screen, from the same offset table the clip
 *             was cut with, and its distance from the event.
 *
 * Driven by requestAnimationFrame writing text straight into the DOM rather than state:
 * `timeupdate` fires ~4×/s, far too coarse to line up a frame, and re-rendering the panel at
 * 60 fps for a label would be waste.
 */
function PlaybackTimes({ videoRef, rows, eventMs }: {
  videoRef: RefObject<HTMLVideoElement>;
  rows: OffsetRow[] | null;
  eventMs: number;
}) {
  const playingRef = useRef<HTMLSpanElement>(null);
  const deltaRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      const wall = v && rows && rows.length ? playerToWall(rows, v.currentTime || 0) : null;
      if (playingRef.current) {
        // The day is printed once, beside the event; repeat it only if the frame is on another.
        playingRef.current.textContent = wall == null ? '—'
          : formatDay(wall) === formatDay(eventMs) ? formatTime(wall) : `${formatDay(wall)} ${formatTime(wall)}`;
      }
      if (deltaRef.current) {
        const d = wall != null ? (wall - eventMs) / 1000 : null;
        deltaRef.current.textContent = d != null ? `${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(2)}s` : '';
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef, rows, eventMs]);

  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2 font-mono tabular-nums">
      <span title="Event time — event_timestamp from the event record">
        <span className="text-amber-500">Event</span> {formatTime(eventMs)}
      </span>
      <span title="Recorded time of the frame on screen, and its distance from the event">
        <span className="text-sky-500">Playing</span> <span ref={playingRef} className="text-foreground">—</span>{' '}
        <span ref={deltaRef} />
      </span>
    </span>
  );
}

/** Small icon button for the title bar. */
function HeaderButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Tip label={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        {children}
      </button>
    </Tip>
  );
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** Local date: 25-09-2026 — the same order the cameras burn into the picture. */
function formatDay(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** Local time to the millisecond: 14:09:09.865 */
function formatTime(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
